import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Input from 'components/FormFields/Input';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  FormWrapper,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
} from 'components/FormModal';
import { AppContext } from 'shared/AppContext';
import {
  useCreatePromo,
  useGetPromo,
  useUpdatePromo,
} from './gql/usePromoMutations';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const InfoText = styled.p`
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: -8px 0 0 0;
  text-align: center;
`;

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
`;

const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  accent-color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

const PromoFormModal = ({ isOpen, onClose, promoId = null }) => {
  const [modalRoot, setModalRoot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [name, setName] = useState('');
  const [ticketDiscount, setTicketDiscount] = useState('');
  const [serviceDiscount, setServiceDiscount] = useState('');
  const [limit, setLimit] = useState('');
  const [expiration, setExpiration] = useState('');
  const [noExpiration, setNoExpiration] = useState(false);

  const {
    state: { accountId },
  } = useContext(AppContext);

  // Get promo data if editing
  const { data: promoData } = useGetPromo(promoId);
  const promo = promoData?.getPromo;

  // Mutations
  const { createPromo, loading: createLoading } = useCreatePromo();
  const { updatePromo, loading: updateLoading } = useUpdatePromo();

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Reset form when modal opens/closes or promo changes
  useEffect(() => {
    if (isOpen) {
      if (promoId && promo) {
        // Edit mode - populate with promo data
        setName(promo.name || '');
        setTicketDiscount(
          promo.ticket_discount != null ? String(promo.ticket_discount) : ''
        );
        setServiceDiscount(
          promo.service_discount != null ? String(promo.service_discount) : ''
        );
        setLimit(promo.limit != null ? String(promo.limit) : '');
        setExpiration(
          promo.expiration
            ? moment(promo.expiration, 'MM-DD-YYYY').format('YYYY-MM-DD')
            : ''
        );
        setNoExpiration(promo.no_expiration || false);
      } else {
        // Create mode - reset form
        setName('');
        setTicketDiscount('');
        setServiceDiscount('');
        setLimit('');
        setExpiration('');
        setNoExpiration(false);
      }
      setErrors({});
    }
  }, [isOpen, promoId, promo]);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Discount code name is required';
    }

    if (!ticketDiscount) {
      newErrors.ticketDiscount = 'Ticket discount is required';
    } else if (
      isNaN(parseFloat(ticketDiscount)) ||
      parseFloat(ticketDiscount) < 0 ||
      parseFloat(ticketDiscount) > 100
    ) {
      newErrors.ticketDiscount = 'Must be between 0 and 100';
    }

    if (
      serviceDiscount &&
      (isNaN(parseFloat(serviceDiscount)) ||
        parseFloat(serviceDiscount) < 0 ||
        parseFloat(serviceDiscount) > 100)
    ) {
      newErrors.serviceDiscount = 'Must be between 0 and 100';
    }

    if (limit && isNaN(parseInt(limit))) {
      newErrors.limit = 'Must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const input = {
        ...(promoId ? { id: promoId } : {}),
        name: name.trim(),
        ticket_discount: parseFloat(ticketDiscount),
        service_discount: serviceDiscount ? parseFloat(serviceDiscount) : 0,
        limit: limit ? parseInt(limit) : null,
        expiration:
          expiration && !noExpiration
            ? moment(expiration).format('MM-DD-YYYY')
            : null,
        no_expiration: noExpiration,
        account_id: accountId,
      };

      const response = promoId
        ? await updatePromo(input)
        : await createPromo(input);

      if (!response || response.errors) {
        toast.error(
          promoId ? 'Error updating discount' : 'Error creating discount'
        );
        return;
      }

      toast.success(
        promoId
          ? 'Discount updated successfully'
          : 'Discount created successfully'
      );
      onClose();
    } catch (error) {
      toast.error(
        promoId ? 'Error updating discount' : 'Error creating discount'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !modalRoot) return null;

  // Don't render if editing but promo data hasn't loaded yet
  if (promoId && !promo) return null;

  const isLoading = isSubmitting || createLoading || updateLoading;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="dialog"
          aria-labelledby="promo-form-modal-title"
          aria-modal="true"
          maxWidth="500px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
            disabled={isLoading}
          >
            <CloseIcon />
          </CloseButton>

          <FormWrapper onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle id="promo-form-modal-title">
                {promoId ? 'Edit Discount Code' : 'Create Discount Code'}
              </ModalTitle>
              <ModalSubtitle>
                {promoId
                  ? 'Update the discount code information below'
                  : 'Fill out the form to create a new discount code'}
              </ModalSubtitle>
            </ModalHeader>

            <ModalContent>
              <FormSection>
                <Input
                  label="Discount Code Name"
                  required
                  placeholder="Enter discount code name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />

                <FormRow>
                  <Input
                    label="Ticket Discount %"
                    required
                    placeholder="0 - 100"
                    type="number"
                    min="0"
                    max="100"
                    value={ticketDiscount}
                    onChange={(e) => setTicketDiscount(e.target.value)}
                    error={!!errors.ticketDiscount}
                    helperText={errors.ticketDiscount}
                  />
                  <Input
                    label="Fee Discount %"
                    placeholder="0 - 100"
                    type="number"
                    min="0"
                    max="100"
                    value={serviceDiscount}
                    onChange={(e) => setServiceDiscount(e.target.value)}
                    error={!!errors.serviceDiscount}
                    helperText={errors.serviceDiscount}
                  />
                </FormRow>

                <InfoText>Transaction fees may apply</InfoText>

                <Input
                  label="Use Limit"
                  placeholder="Number of times code can be used"
                  type="number"
                  min="0"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  error={!!errors.limit}
                  helperText={errors.limit}
                />

                <DateInputContainer>
                  <DateLabel>Expiration Date</DateLabel>
                  <DateTimePicker
                    showTime={false}
                    value={
                      expiration ? new Date(expiration + 'T12:00:00') : null
                    }
                    onChange={(date) => setExpiration(date || '')}
                  />
                </DateInputContainer>

                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    checked={noExpiration}
                    onChange={(e) => {
                      setNoExpiration(e.target.checked);
                      if (e.target.checked) {
                        setExpiration('');
                      }
                    }}
                  />
                  <CheckboxLabel>
                    No expiration date for this code
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormSection>
            </ModalContent>

            <ModalFooter>
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isLoading}>
                {isLoading
                  ? 'Saving...'
                  : promoId
                  ? 'Update Discount'
                  : 'Create Discount'}
              </PrimaryButton>
            </ModalFooter>
          </FormWrapper>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

export default PromoFormModal;
